
import { useState } from 'react';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { EmailLog, useEmailLogs } from '@/hooks/admin/email/useEmailLogs';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

// Helper function to get recipient email with fallback
const getRecipientEmail = (log: EmailLog): string => {
  // Priority: recipient_email > profiles.email > user_id (as fallback)
  if (log.recipient_email) {
    return log.recipient_email;
  }
  if (log.profiles?.email) {
    return log.profiles.email;
  }
  if (log.user_id) {
    return `User ID: ${log.user_id.substring(0, 8)}...`;
  }
  return 'Unknown recipient';
};

// Helper function to format email type for display
const formatEmailType = (emailType: string): string => {
  if (!emailType || emailType === 'general') {
    return 'General';
  }
  
  const typeMap: Record<string, string> = {
    'authSignup': 'Welcome Email',
    'authLogin': 'Login Notification',
    'resetPassword': 'Password Reset',
    'verifyEmail': 'Email Verification',
    'documentVerified': 'Document Verified',
    'documentRejected': 'Document Rejected',
    'pollsterStatusUpdated': 'Pollster Status',
    'newPollPublished': 'New Poll',
    'pollClosed': 'Poll Closed',
    'insightUpdated': 'Insight Updated',
    'waitlistLaunch': 'Waitlist Launch',
    'waitlistConfirmation': 'Waitlist Confirmation',
    'accountSuspended': 'Account Suspended',
    'accountReactivated': 'Account Reactivated',
    'accountDeletionRequest': 'Deletion Request',
    'accountDeletionConfirmed': 'Deletion Confirmed',
    'accountDeletionCompleted': 'Deletion Completed',
    'custom': 'Custom Email'
  };
  
  return typeMap[emailType] || emailType;
};

export const EmailLogsTable = () => {
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const isMobile = useIsMobile();

  const { data, isLoading, isError, refetch, isRefetching } = useEmailLogs(page, pageSize);

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (data?.logs && data.logs.length === pageSize) {
      setPage(page + 1);
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  if (isLoading) {
    return <div className="py-10 text-center text-gray-900 dark:text-gray-100">Loading email logs...</div>;
  }

  if (isError) {
    return (
      <div className="py-10 text-center">
        <div className="text-red-500 mb-4">Error loading email logs. Please try again.</div>
        <Button 
          onClick={handleRefresh} 
          variant="outline"
          className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <RefreshCw className="mr-2 h-4 w-4" /> Retry
        </Button>
      </div>
    );
  }

  if (!data?.logs || data.logs.length === 0) {
    return (
      <div className="py-10 text-center">
        <div className="mb-4 text-gray-900 dark:text-gray-100">No email logs found</div>
        <Button 
          onClick={handleRefresh} 
          variant="outline"
          className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>
    );
  }

  // Render a card-like view for mobile
  if (isMobile) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Email Logs</h3>
          <Button 
            onClick={handleRefresh} 
            variant="outline" 
            disabled={isRefetching}
            size="sm"
            className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${isRefetching ? 'animate-spin' : ''}`} /> 
            {isRefetching ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        <div className="space-y-4">
          {data.logs.map((log) => {
            const hasError = log.error !== undefined && log.error !== null && log.error !== '';
            const isPending = log.status === 'pending';
            const recipientEmail = getRecipientEmail(log);
            const formattedType = formatEmailType(log.email_type);
            
            return (
              <div key={log.id} className="border rounded-md p-3 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{formattedType}</span>
                  <Badge 
                    variant={
                      hasError ? "destructive" : 
                      isPending ? "outline" : 
                      "default"
                    }
                    className={
                      hasError ? "" : 
                      isPending ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-300" : 
                      "bg-green-500 hover:bg-green-600 text-white"
                    }
                  >
                    {hasError ? 'Failed' : isPending ? 'Pending' : 'Sent'}
                  </Badge>
                </div>
                
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Time:</span>
                    <span className="text-gray-900 dark:text-gray-100">{format(new Date(log.sent_at), 'MMM d, yyyy HH:mm')}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Recipient:</span>
                    <span className="truncate max-w-[180px] text-gray-900 dark:text-gray-100">{recipientEmail}</span>
                  </div>

                  {log.subject && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subject:</span>
                      <span className="truncate max-w-[180px] text-gray-900 dark:text-gray-100">{log.subject}</span>
                    </div>
                  )}
                </div>
                
                {hasError && (
                  <div className="mt-2 text-sm bg-red-50 dark:bg-red-900/20 p-2 rounded text-red-600 dark:text-red-300 border border-red-200 dark:border-red-800">
                    {String(log.error)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        <div className="flex justify-between items-center mt-4">
          <Button 
            variant="outline" 
            onClick={handlePreviousPage}
            disabled={page === 1}
            size="sm"
            className="px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <span className="text-sm text-gray-900 dark:text-gray-100">Page {page}</span>
          <Button 
            variant="outline" 
            onClick={handleNextPage}
            disabled={!data.logs || data.logs.length < pageSize}
            size="sm"
            className="px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  // Desktop view with table
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Email Delivery Logs</h3>
        <Button 
          onClick={handleRefresh} 
          variant="outline" 
          disabled={isRefetching}
          size="sm"
          className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${isRefetching ? 'animate-spin' : ''}`} /> 
          {isRefetching ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>
      
      <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-200 dark:border-gray-700">
              <TableHead className="text-gray-900 dark:text-gray-100 font-medium">Time</TableHead>
              <TableHead className="text-gray-900 dark:text-gray-100 font-medium">Type</TableHead>
              <TableHead className="text-gray-900 dark:text-gray-100 font-medium">Subject</TableHead>
              <TableHead className="text-gray-900 dark:text-gray-100 font-medium">Recipient</TableHead>
              <TableHead className="text-gray-900 dark:text-gray-100 font-medium">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.logs.map((log) => {
              const hasError = log.error !== undefined && log.error !== null && log.error !== '';
              const isPending = log.status === 'pending';
              const recipientEmail = getRecipientEmail(log);
              const formattedType = formatEmailType(log.email_type);
              
              return (
                <TableRow key={log.id} className="border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <TableCell className="text-gray-900 dark:text-gray-100">
                    {format(new Date(log.sent_at), 'MMM d, yyyy HH:mm:ss')}
                  </TableCell>
                  <TableCell className="text-gray-900 dark:text-gray-100">{formattedType}</TableCell>
                  <TableCell className="text-gray-900 dark:text-gray-100 max-w-[200px] truncate">
                    {log.subject || 'No subject'}
                  </TableCell>
                  <TableCell className="text-gray-900 dark:text-gray-100">{recipientEmail}</TableCell>
                  <TableCell>
                    <Badge 
                      variant={
                        hasError ? "destructive" : 
                        isPending ? "outline" : 
                        "default"
                      }
                      className={
                        hasError ? "" : 
                        isPending ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-300" : 
                        "bg-green-500 hover:bg-green-600 text-white"
                      }
                    >
                      {hasError ? 'Failed' : isPending ? 'Pending' : 'Sent'}
                    </Badge>
                    {hasError && (
                      <span className="text-sm text-red-500 block mt-1">
                        {String(log.error)}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-between items-center mt-4">
        <Button 
          variant="outline" 
          onClick={handlePreviousPage}
          disabled={page === 1}
          size="sm"
          className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Previous
        </Button>
        <span className="text-sm text-gray-900 dark:text-gray-100">Page {page} of {Math.ceil((data?.total || 0) / pageSize)}</span>
        <Button 
          variant="outline" 
          onClick={handleNextPage}
          disabled={!data.logs || data.logs.length < pageSize}
          size="sm"
          className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Next <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};
